export interface ThawProductionInputLike {
  source_production_id?: string | null;
}

export interface ThawProductionLike {
  id: string;
  inputs?: ReadonlyArray<ThawProductionInputLike>;
}

export interface ThawLabelEventLike {
  payload?: {
    preset?: unknown;
    data?: Record<string, unknown> | null;
  } | null;
}

function productionIdFromLabel(event: ThawLabelEventLike): string | null {
  if (event.payload?.preset !== 'decongelato') return null;
  const productionId = event.payload.data?.production_id;
  return typeof productionId === 'string' && productionId.length > 0
    ? productionId
    : null;
}

/**
 * Returns production ids whose label proves that the output was thawed.
 * This covers raw-food thawing, where there is no source production pointer.
 */
export function collectThawedProductionIds(
  events: ReadonlyArray<ThawLabelEventLike>,
): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const event of events) {
    const productionId = productionIdFromLabel(event);
    if (productionId) ids.add(productionId);
  }
  return ids;
}

/**
 * A production is not a candidate for another thaw only when its own output
 * is explicitly marked as thawed. `source_production_id` is also used for
 * ordinary semilavorato traceability, so it is not sufficient evidence that
 * the production itself was thawed.
 */
export function isThawedProduction(
  production: ThawProductionLike,
  thawedProductionIds: ReadonlySet<string>,
): boolean {
  return thawedProductionIds.has(production.id);
}

/**
 * Extract parent production ids from thaw labels. It supplements the
 * source_production_id input marker for legacy/offline rows.
 */
export function collectThawedParentProductionIds(
  events: ReadonlyArray<ThawLabelEventLike>,
): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const event of events) {
    if (event.payload?.preset !== 'decongelato') continue;
    const parentId = event.payload.data?.parent_production_id;
    if (typeof parentId === 'string' && parentId.length > 0) ids.add(parentId);
  }
  return ids;
}

interface FrozenPreparationLike extends ThawProductionLike {
  recipe_id?: string | null;
  archived_at?: string | null;
  expires_at?: string | null;
  produced_at?: string | null;
}

/** Solo il flag della ricetta determina se una preparazione è da scongelare. */
export function selectFrozenProductions<T extends FrozenPreparationLike>(
  productions: readonly T[],
  options: {
    recipes: readonly { id: string; freezing_enabled?: boolean }[];
    labeledProductionIds: ReadonlySet<string>;
    thawedProductionIds: ReadonlySet<string>;
    warehouseEnabled: boolean;
    lots: readonly { source_production_id?: string | null; available: number }[];
    now: number;
  },
): T[] {
  const frozenRecipeIds = new Set(options.recipes
    .filter((recipe) => recipe.freezing_enabled === true)
    .map((recipe) => recipe.id));
  return productions.filter((production) => {
    if (!production.recipe_id || !frozenRecipeIds.has(production.recipe_id)) return false;
    if (production.archived_at) return false;
    if (production.expires_at && Date.parse(production.expires_at) < options.now) return false;
    if (isThawedProduction(production, options.thawedProductionIds)) return false;
    if (!options.labeledProductionIds.has(production.id)) return false;
    return !options.warehouseEnabled || options.lots.some((lot) =>
      lot.source_production_id === production.id && lot.available > 0.0001);
  }).sort((a, b) => (b.produced_at ?? '').localeCompare(a.produced_at ?? ''));
}
